import React, { useState } from "react";
import { Phone, Copy, Check, Instagram, MessageCircle, MessageSquare } from "lucide-react";

/**
 * Premium contact actions row.
 *  - Shows the phone number only if `revealed` is true.
 *  - "Call" -> tel: deep link.
 *  - "Copy" -> clipboard with success state.
 *  - WhatsApp + Viber + Instagram deep links.
 */
export default function ContactActions({ phone, hasPhone = false, instagram, whatsapp, viber, revealed = true, lang = "en", className = "" }) {
  const [copied, setCopied] = useState(false);
  const showLocked = !revealed && hasPhone;
  if (!phone && !instagram && !whatsapp && !viber && !showLocked) return null;
  const copy = async () => {
    if (!phone) return;
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {}
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="contact-actions">
      {revealed && phone ? (
        <>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-1.5 btn-primary px-4 py-2 text-xs"
            data-testid="contact-call"
          >
            <Phone className="w-3.5 h-3.5" />
            {lang === "bg" ? "Позвъни" : "Call coach"}
          </a>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 btn-secondary px-3 py-2 text-xs"
            data-testid="contact-copy"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-pitch" /> : <Copy className="w-3.5 h-3.5" />}
            {copied
              ? (lang === "bg" ? "Копирано" : "Copied")
              : (lang === "bg" ? "Копирай" : "Copy number")}
          </button>
          <span className="font-mono text-[11px] text-white/55 tracking-wider" data-testid="contact-phone">{phone}</span>
        </>
      ) : showLocked ? (
        <span className="inline-flex items-center gap-1.5 chip-badge" data-testid="contact-locked">
          <Phone className="w-3 h-3" />
          {lang === "bg" ? "Достъпно след резервация" : "Available after booking"}
        </span>
      ) : null}
      {revealed && whatsapp && (
        <a
          href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-blue-400/40 hover:bg-blue-400/10 text-blue-300 rounded-md transition-colors"
          data-testid="contact-whatsapp"
        >
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
        </a>
      )}
      {revealed && viber && (
        <a
          href={`viber://chat?number=${encodeURIComponent(viber)}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-purple-400/40 hover:bg-purple-400/10 text-purple-300 rounded-md transition-colors"
          data-testid="contact-viber"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Viber
        </a>
      )}
      {instagram && (
        <a
          href={instagram.startsWith("http") ? instagram : `https://instagram.com/${instagram.replace(/^@/, "")}`}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 btn-secondary px-3 py-2 text-xs"
          data-testid="contact-instagram"
        >
          <Instagram className="w-3.5 h-3.5" />
          {lang === "bg" ? "Instagram" : "Instagram"}
        </a>
      )}
    </div>
  );
}
